import { useState } from 'react';
import { ShieldCheck, Plus, KeyRound, Copy, Check } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { useApi } from '@/lib/useApi';
import { useAuth } from '@/lib/auth';
import { relative } from '@/lib/format';
import {
  Badge, Button, Card, CardHeader, Field, Input, LoadingBlock, Modal,
  PageHeader, Table, TableWrap, Td, Th, useToast,
} from '@/components/ui';
import type { PlatformAdmin, AdminResetLink } from '@shared/types';

/**
 * Who can open the console.
 *
 * ── Why adding one does not set a password ────────────────────────────────
 *
 * The new operator gets a link and chooses their own, so the password never
 * exists in a mailbox or in the head of whoever created the account — and an
 * account cannot sit unused with a password somebody else picked.
 *
 * The link is also shown on screen, because a deployment with no mail
 * configured must not be a deployment where nobody can be added.
 */
export default function Operators() {
  const { admin } = useAuth();
  const toast = useToast();
  const [adding, setAdding] = useState(false);
  const [issued, setIssued] = useState<AdminResetLink | null>(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ full_name: '', email: '' });

  const { data, loading, reload } = useApi<PlatformAdmin[]>('/admin/admins');
  const rows = data ?? [];

  async function create() {
    setBusy(true);
    try {
      const res = await api.post<PlatformAdmin & AdminResetLink>('/admin/admins', form);
      setAdding(false);
      setForm({ full_name: '', email: '' });
      setIssued({
        sent_to: res.email,
        delivery: res.delivery,
        link: res.link,
        expires_in_minutes: res.expires_in_minutes,
      });
      reload();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'That operator could not be added');
    } finally {
      setBusy(false);
    }
  }

  async function sendReset(row: PlatformAdmin) {
    setBusy(true);
    try {
      setIssued(await api.post<AdminResetLink>(`/admin/admins/${row.id}/reset-link`));
      reload();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'That link could not be issued');
    } finally {
      setBusy(false);
    }
  }

  async function setActive(row: PlatformAdmin, is_active: boolean) {
    try {
      await api.patch(`/admin/admins/${row.id}`, { is_active });
      reload();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'That could not be changed');
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Operators"
        subtitle="Everyone who can open this console. Each of them can see every customer account."
        action={
          <Button variant="primary" icon={<Plus className="h-4 w-4" />} onClick={() => setAdding(true)}>
            Add an operator
          </Button>
        }
      />

      {loading && !data ? <LoadingBlock rows={4} /> : (
        <Card className="p-0 overflow-hidden">
          <CardHeader title="Console accounts" subtitle={`${rows.length} in total`} />
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <Th>Name</Th>
                  <Th>Email</Th>
                  <Th>Last signed in</Th>
                  <Th />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id} className="border-t border-line">
                    <Td>
                      <span className="flex items-center gap-2">
                        <ShieldCheck className="h-4 w-4 text-faint shrink-0" />
                        {row.full_name || '—'}
                        {row.id === admin?.id && <Badge tone="neutral">You</Badge>}
                        {!row.is_active && <Badge tone="warn">Disabled</Badge>}
                      </span>
                    </Td>
                    <Td className="text-muted">{row.email}</Td>
                    <Td className="text-muted whitespace-nowrap">
                      {row.last_login_at ? relative(row.last_login_at) : 'Never'}
                    </Td>
                    <Td>
                      <div className="flex justify-end gap-1.5">
                        <Button
                          size="sm"
                          icon={<KeyRound className="h-3.5 w-3.5" />}
                          loading={busy}
                          disabled={!row.is_active}
                          onClick={() => void sendReset(row)}
                        >
                          {row.last_login_at ? 'Reset link' : 'Re-send invite'}
                        </Button>
                        {/*
                          Deactivating the last way in is refused by the API,
                          not hidden here — the message it gives is better than
                          a greyed-out button with no explanation.
                        */}
                        <Button
                          size="sm"
                          onClick={() => void setActive(row, !row.is_active)}
                        >
                          {row.is_active ? 'Disable' : 'Enable'}
                        </Button>
                      </div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        </Card>
      )}

      <Modal
        open={adding}
        onClose={() => setAdding(false)}
        title="Add an operator"
        subtitle="They choose their own password from a link. Nothing is set here."
        footer={(
          <>
            <Button onClick={() => setAdding(false)}>Cancel</Button>
            <Button
              variant="primary"
              loading={busy}
              disabled={!form.email.trim() || !form.full_name.trim()}
              onClick={() => void create()}
            >
              Add and send the link
            </Button>
          </>
        )}
      >
        <div className="space-y-4">
          <Field label="Name" required>
            <Input
              value={form.full_name}
              onChange={(e) => setForm((f) => ({ ...f, full_name: e.target.value }))}
              autoFocus
            />
          </Field>
          <Field label="Email" required hint="Where the link goes, and their username.">
            <Input
              type="email"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            />
          </Field>
          <p className="text-sm text-muted leading-relaxed">
            The console reaches every customer account — their tenants, their money, their
            documents. Add somebody here only when that is what you mean.
          </p>
        </div>
      </Modal>

      <LinkIssued issued={issued} onClose={() => setIssued(null)} />
    </div>
  );
}

/** Shows the link itself, for when mail is not configured or has not arrived. */
function LinkIssued({ issued, onClose }: { issued: AdminResetLink | null; onClose: () => void }) {
  const toast = useToast();
  const [copied, setCopied] = useState(false);

  if (!issued) return null;

  return (
    <Modal
      open
      onClose={onClose}
      title="Link issued"
      subtitle={issued.delivery === 'sent'
        ? `Emailed to ${issued.sent_to}.`
        : `It could not be emailed (${issued.delivery}), so hand it over yourself.`}
      footer={<Button variant="primary" onClick={onClose}>Done</Button>}
    >
      <div className="space-y-3">
        <p className="text-sm text-muted leading-relaxed">
          It expires in {issued.expires_in_minutes} minutes and works once.
        </p>
        <div className="flex gap-2">
          <Input readOnly value={issued.link} className="font-mono text-xs" />
          <Button
            icon={copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            onClick={() => {
              navigator.clipboard.writeText(issued.link).then(
                () => { setCopied(true); toast.success('Copied'); },
                () => toast.error('Could not copy — select it by hand'),
              );
            }}
          >
            Copy
          </Button>
        </div>
      </div>
    </Modal>
  );
}
